/**
 * Asset utility functions for managing images and icons
 */

// Base path for assets
const ASSETS_BASE_PATH = '/assets';

/**
 * Get the full path for an image asset
 */
export const getImagePath = (filename: string): string => {
  return `${ASSETS_BASE_PATH}/images/${filename}`;
};

/**
 * Get the full path for an icon asset
 */
export const getIconPath = (filename: string): string => {
  return `${ASSETS_BASE_PATH}/icons/${filename}`;
};

/**
 * Get the full path for a poster image
 */
export const getPosterPath = (filename: string): string => {
  return `${ASSETS_BASE_PATH}/images/${filename}`;
};

/**
 * Poster image utilities
 */
export const POSTER_IMAGES = {
  BANANA: getPosterPath('poster_banana.jpg'),
  PIZZA: getPosterPath('poster_pizza.jpg'),
  CINEMA: getPosterPath('poster_cinema.jpg'),
  CAKE: getPosterPath('poster_cake.jpg'),
  COCKTAIL_PARTY: getPosterPath('poster_cocktail_party.jpg'),
  CONCERT: getPosterPath('poster_concert.jpg'),
  NIGHT_OUT: getPosterPath('poster_night_out.jpg'),
  WORK: getPosterPath('poster_work.jpg'),
  SPORTS: getPosterPath('poster_sports.jpg'),
} as const;

/**
 * Get poster display name
 */
export const getPosterDisplayName = (imageName: string): string => {
  // Check if it's a custom uploaded image
  if (isCustomImage(imageName)) {
    return 'Custom Image';
  }
  
  switch (imageName) {
    case 'poster_pizza.jpg':
      return 'Pizza Party';
    case 'poster_banana.jpg':
      return 'Funky Party';
    case 'poster_cinema.jpg':
      return 'Movie Night';
    case 'poster_cake.jpg':
      return 'Birthday Party';
    case 'poster_cocktail_party.jpg':
      return 'Cocktail Party';
    case 'poster_concert.jpg':
      return 'Live Concert';
    case 'poster_night_out.jpg':
      return 'Night Out';
    case 'poster_work.jpg':
      return 'Work Meetup';
    case 'poster_sports.jpg':
      return 'Sports Event';
    default:
      return 'Custom Image';
  }
};

/**
 * Get poster emoji
 */
export const getPosterEmoji = (imageName: string): string => {
  // Check if it's a custom uploaded image
  if (isCustomImage(imageName)) {
    return '📷';
  }
  
  switch (imageName) {
    case 'poster_pizza.jpg':
      return '🍕';
    case 'poster_banana.jpg':
      return '🍌';
    case 'poster_cinema.jpg':
      return '🎬';
    case 'poster_cake.jpg':
      return '🎂';
    case 'poster_cocktail_party.jpg':
      return '🍸';
    case 'poster_concert.jpg':
      return '🎵';
    case 'poster_night_out.jpg':
      return '🌃';
    case 'poster_work.jpg':
      return '💼';
    case 'poster_sports.jpg':
      return '⚽';
    default:
      return '📷';
  }
};

/**
 * Check if an image is a custom uploaded image (Firebase Storage URL)
 */
export const isCustomImage = (imageName: string): boolean => {
  return imageName.includes('firebasestorage.googleapis.com') && 
         imageName.includes('posters');
};

/**
 * Get poster path from image name
 */
export const getPosterPathFromName = (imageName: string): string => {
  // Check if it's a custom uploaded image (Firebase Storage URL)
  if (isCustomImage(imageName)) {
    return imageName; // Return the URL directly
  }
  return getPosterPath(imageName);
};

/**
 * Predefined asset paths
 */
export const ASSETS = {
  // Images
  LOGO: getImagePath('logo_cutout.png'),
  HOME_SCREEN: getImagePath('home.png'),
  EVENT_CREATION_SCREEN: getImagePath('event-creation.png'),
  EVENT_LIST_SCREEN: getImagePath('event-list.png'),
  EVENT_SCREEN: getImagePath('event.png'),
  
  // Poster Images
  POSTERS: POSTER_IMAGES,
  
  // Icons (placeholder paths - add actual icons as needed)
  FAVICON: getIconPath('favicon.ico'),
  APPLE_TOUCH_ICON: getIconPath('apple-touch-icon.png'),
  
  // Social icons (placeholder paths)
  FACEBOOK: getIconPath('facebook.svg'),
  TWITTER: getIconPath('twitter.svg'),
  INSTAGRAM: getIconPath('instagram.svg'),
  WHATSAPP: getIconPath('whatsapp.svg'),
} as const;

/**
 * Preload an image asset
 */
export const preloadImage = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = src;
  });
};

/**
 * Preload multiple image assets
 */
export const preloadImages = async (srcs: string[]): Promise<void> => {
  await Promise.all(srcs.map(preloadImage));
};
